'use client'

import { useMemo, useState } from 'react'
import { Eye, EyeOff, Plus } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogFooter,
  EntityListShell,
  Input,
  Label,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  formatDate,
} from '@rr/ui'
import {
  useCompanyUsers,
  useCreateCompanyUser,
} from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function CompanyUsersList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyUsers(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <div className="flex items-center gap-2 w-full">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            placeholder="Search by name or email…"
            className="max-w-sm"
          />
          <div className="ml-auto">
            <Button size="sm" leftIcon={Plus} onClick={() => setCreateOpen(true)}>
              Add user
            </Button>
          </div>
          <CreateCompanyUserDialog
            companyId={companyId}
            open={createOpen}
            onClose={() => setCreateOpen(false)}
          />
        </div>
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load users.'
          : 'No users in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium text-foreground">
                  {u.name ?? '—'}
                </TableCell>
                <TableCell className="text-sm">{u.email ?? '—'}</TableCell>
                <TableCell className="text-sm">
                  {u.department?.name ?? '—'}
                </TableCell>
                <TableCell>
                  <StatusChip status={(u.status ?? 'active') as any} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(u.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}

function CreateCompanyUserDialog({
  companyId,
  open,
  onClose,
}: {
  companyId: string
  open: boolean
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const createMutation = useCreateCompanyUser(companyId)

  const passwordsMatch = password === confirmPassword
  const confirmTouched = confirmPassword.length > 0
  const submitDisabled =
    createMutation.isPending ||
    !name.trim() ||
    !email.trim() ||
    password.length < 8 ||
    !passwordsMatch

  function reset() {
    setName('')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setShowPassword(false)
    setShowConfirmPassword(false)
  }
  function handleClose() {
    reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Add user"
      subtitle="Create a login for someone inside this organization."
      maxWidth="md"
    >
      <form
        id="create-company-user-form"
        onSubmit={async (e) => {
          e.preventDefault()
          if (submitDisabled) return
          try {
            await createMutation.mutateAsync({
              name: name.trim(),
              email: email.trim(),
              password,
            })
            toast(`User "${name.trim()}" created`, 'success')
            handleClose()
          } catch (err) {
            toast(
              err instanceof Error ? err.message : 'Failed to create user',
              'error',
            )
          }
        }}
        className="grid gap-4"
      >
        <div className="grid gap-1.5">
          <Label htmlFor="cu-name">Name</Label>
          <Input
            id="cu-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Aman Aşyrow"
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="cu-email">Email</Label>
          <Input
            id="cu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="aman@company.com"
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="cu-password">Temporary password</Label>
          <div className="relative">
            <Input
              id="cu-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="cu-confirm-password">Confirm password</Label>
          <div className="relative">
            <Input
              id="cu-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter the password"
              required
              minLength={8}
              className="pr-10"
              aria-invalid={confirmTouched && !passwordsMatch}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {confirmTouched && !passwordsMatch && (
            <p className="text-xs text-destructive">Passwords don&apos;t match.</p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-company-user-form"
            disabled={submitDisabled}
          >
            {createMutation.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
